<template>
    <div v-if="show && loaded" class="nevs-popup-container">
        <div class="nevs-popup">
            <div class="popup-form">
                <NevsTextField v-model="name" :error="validation.name"
                               :label="'*' + $LANG.Get('fields.name') + ':'"></NevsTextField>
                <NevsNumberField v-model="capacity" :decimal-places="0" :error="validation.capacity"
                                 :label="'*' + $LANG.Get('fields.capacity') + ':'"></NevsNumberField>
                <div class="nevs-field">
                    <span class="nevs-field-label">{{ $LANG.Get('fields.color') }}:</span>
                    <div class="color-swatches">
                        <span v-for="swatch in colors" :key="swatch" class="color-swatch"
                              :class="{'color-swatch-selected': swatch === color}" :style="{backgroundColor: swatch}"
                              @click="color = swatch"></span>
                    </div>
                </div>
                <NevsCheckbox v-model="active" :label="$LANG.Get('fields.activeM')"></NevsCheckbox>
                <NevsCheckbox v-model="hiddenUnlessRegistered" :label="$LANG.Get('fields.hiddenUnlessRegistered')"></NevsCheckbox>

                <h3 class="session-type-section-heading">{{ $LANG.Get('labels.requiredMemberships') }}</h3>
                <table class="session-type-memberships-table">
                    <tbody>
                        <tr v-for="(sessionTypeMembership, key) in memberships" :key="key">
                            <td><NevsSelect v-model="sessionTypeMembership.membership_id" :ajax="'select/memberships'"></NevsSelect></td>
                            <td class="session-type-memberships-action"><span :title="$LANG.Get('tooltips.delete')"
                                  class="session-type-membership-button" @click="removeMembership(key)"><i
                                class="fa-solid fa-trash"></i></span></td>
                        </tr>
                    </tbody>
                </table>
                <NevsButton style="margin-top: 10px;" @click="addMembership()"><i class="fa-solid fa-plus"></i> {{ $LANG.Get('buttons.addMembership') }}</NevsButton>
            </div>
            <div class="nevs-popup-buttons">
                <NevsButton class="error" @click="cancelClick()">
                    <i class="fa-solid fa-xmark"></i>
                    {{ $LANG.Get('buttons.cancel') }}
                </NevsButton>
                <NevsButton class="success" @click="okClick()">
                    <i class="fa-solid fa-check"></i>
                    {{ $LANG.Get('buttons.ok') }}
                </NevsButton>
            </div>
        </div>
    </div>
</template>

<script>
import NevsButton from "@/components/nevs/NevsButton.vue";
import NevsNumberField from "@/components/nevs/NevsNumberField.vue";
import NevsTextField from "@/components/nevs/NevsTextField.vue";
import NevsCheckbox from "@/components/nevs/NevsCheckbox.vue";
import NevsSelect from "@/components/nevs/NevsSelect.vue";

// Predefined palette offered when creating/editing a session type. Colours are stored as hex strings.
const SESSION_TYPE_COLORS = [
    '#e57373', '#f06292', '#ba68c8', '#9575cd',
    '#7986cb', '#64b5f6', '#4fc3f7', '#4dd0e1',
    '#4db6ac', '#81c784', '#aed581', '#dce775',
    '#ffd54f', '#ffb74d', '#ff8a65', '#a1887f'
];

export default {
    name: "ModalEditSessionType",
    components: {NevsSelect, NevsCheckbox, NevsTextField, NevsNumberField, NevsButton},
    props: {
        show: {
            type: Boolean,
            default: false
        },
        sessionType: {
            type: Object,
            default: null
        }
    },
    emits: ['done'],
    data() {
        return {
            loaded: false,
            colors: SESSION_TYPE_COLORS,
            name: '',
            capacity: 1,
            color: SESSION_TYPE_COLORS[0],
            active: true,
            hiddenUnlessRegistered: false,
            memberships: [],
            validation: {
                name: '',
                capacity: ''
            }
        }
    },
    watch: {
        show() {
            this.loaded = false;
            this.validation.name = '';
            this.validation.capacity = '';
            if (this.show) {
                this.memberships = [];
                if (this.sessionType !== null) {
                    this.name = this.sessionType.name;
                    this.capacity = this.sessionType.capacity;
                    this.color = this.sessionType.color;
                    this.active = this.sessionType.active;
                    this.hiddenUnlessRegistered = !!this.sessionType.hidden_unless_registered;
                    this.loadMemberships(this.sessionType.id);
                } else {
                    this.name = '';
                    this.capacity = 1;
                    this.color = SESSION_TYPE_COLORS[0];
                    this.active = true;
                    this.hiddenUnlessRegistered = false;
                }
                this.$nextTick(() => {
                    this.loaded = true;
                });
            }
        }
    },
    methods: {
        loadMemberships(sessionTypeId) {
            let vm = this;
            this.$API.APICall('get', 'session-types/' + sessionTypeId, {}, (data, success) => {
                if (success && data.session_type && Array.isArray(data.session_type.memberships)) {
                    vm.memberships = JSON.parse(JSON.stringify(data.session_type.memberships));
                }
            });
        },
        addMembership() {
            this.memberships.push({
                membership_id: null
            });
        },
        removeMembership(index) {
            this.memberships.splice(index, 1);
        },
        cancelClick() {
            this.$emit('done', false);
        },
        okClick() {
            let valid = true;
            if (this.name === null || this.name.trim() === '') {
                this.validation.name = this.$LANG.Get('labels.mandatoryField');
                valid = false;
            } else {
                this.validation.name = '';
            }
            if (this.capacity === '' || this.capacity === null || this.capacity <= 0) {
                this.validation.capacity = this.$LANG.Get('labels.mandatoryField');
                valid = false;
            } else {
                this.validation.capacity = '';
            }
            if (!valid) {
                return;
            }

            let vm = this;
            let method = this.sessionType === null ? 'post' : 'put';
            let action = this.sessionType === null ? 'session-types' : 'session-types/' + this.sessionType.id;
            this.$API.APICall(method, action, {
                name: this.name,
                color: this.color,
                capacity: this.capacity,
                active: this.active,
                hidden_unless_registered: this.hiddenUnlessRegistered,
                memberships: this.memberships.filter((membership) => membership.membership_id !== null)
            }, (data, success) => {
                if (success) {
                    vm.$emit('done', true);
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        }
    }
}
</script>

<style scoped lang="scss">
@use '../../scss/vars' as *;

.nevs-popup {
    max-width: 500px;
}

.popup-form {
    padding: 10px;
}

.color-swatches {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 4px;
}

.color-swatch {
    width: 30px;
    height: 30px;
    border-radius: 5px;
    cursor: pointer;
    border: 2px solid transparent;
    box-sizing: border-box;
}

.color-swatch:hover {
    border-color: $sand-400;
}

.color-swatch-selected {
    border-color: $ink-strong;
    box-shadow: 0 0 0 2px $white inset;
}

.session-type-section-heading {
    margin: 15px 0 5px;
    font-size: 16px;
}

.session-type-memberships-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 10px;
}

.session-type-memberships-table th {
    text-align: left;
    font-size: 12px;
    padding: 0 5px;
}

.session-type-memberships-table td {
    padding: 0 5px;
    vertical-align: top;
}

.session-type-memberships-table td.session-type-memberships-action {
    width: 20px;
    vertical-align: middle;
    text-align: center;
}

.session-type-membership-button {
    font-size: 14px;
    cursor: pointer;
}
</style>
